import {
  ADMIN_REQUIRED_ROLE,
  ADMIN_ROLES_CLAIM,
  ADMIN_ROLES_CLAIM_ENFORCEMENT,
} from "../config";

const CONTENT_MANAGER_ROLES = ["admin", "teacher"];
const CLAIM_BACKED_EXTERNAL_PROVIDERS = new Set(["auth0"]);
const DB_BACKED_EXTERNAL_PROVIDERS = new Set(["feide", "vipps"]);
const INTERNAL_AUTH_PROVIDERS = new Set([
  "admin",
  "dev",
  "development",
  "internal",
  "student_gate",
]);

const normalizeRole = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
};

const toClaimList = (value) => {
  if (value == null) return [];
  const values = Array.isArray(value) ? value : [value];
  return values.filter((item) => item != null).map((item) => String(item));
};

const firstPopulatedClaim = (user, claimNames) => {
  if (!user) return [];
  for (const claimName of claimNames) {
    if (!claimName || !(claimName in user)) continue;
    const values = toClaimList(user[claimName]);
    if (values.length) return values;
  }
  return [];
};

const hasConfiguredRolesClaim = (user) =>
  Boolean(
    user &&
    ADMIN_ROLES_CLAIM &&
    Object.prototype.hasOwnProperty.call(user, ADMIN_ROLES_CLAIM),
  );

export const isExternalAuthIdentity = (user) => {
  const provider = normalizeRole(user?.provider);
  if (
    CLAIM_BACKED_EXTERNAL_PROVIDERS.has(provider) ||
    DB_BACKED_EXTERNAL_PROVIDERS.has(provider)
  ) {
    return true;
  }
  if (INTERNAL_AUTH_PROVIDERS.has(provider)) return false;
  return Boolean(provider || user?.iss);
};

export const isInternalAuthIdentity = (user) =>
  INTERNAL_AUTH_PROVIDERS.has(normalizeRole(user?.provider));

export const isDbBackedExternalIdentity = (user) =>
  DB_BACKED_EXTERNAL_PROVIDERS.has(normalizeRole(user?.provider));

export const canUseContentManagerProfileFallback = (
  user,
  enforcement = ADMIN_ROLES_CLAIM_ENFORCEMENT,
) =>
  Boolean(user) &&
  !hasConfiguredRolesClaim(user) &&
  (isInternalAuthIdentity(user) ||
    isDbBackedExternalIdentity(user) ||
    (enforcement === "log_only" && isExternalAuthIdentity(user)));

export const getAllowedContentManagerRoles = () => {
  const roles = new Set(CONTENT_MANAGER_ROLES.map(normalizeRole));
  const requiredRole = normalizeRole(ADMIN_REQUIRED_ROLE);
  if (requiredRole) roles.add(requiredRole);
  return roles;
};

export const extractAuthRoles = (user) => {
  let roles = [];
  if (hasConfiguredRolesClaim(user)) {
    roles = toClaimList(user[ADMIN_ROLES_CLAIM]);
  } else if (isInternalAuthIdentity(user)) {
    roles = firstPopulatedClaim(user, ["roles", "role"]);
  }
  return Array.from(new Set(roles.map(normalizeRole).filter(Boolean)));
};

export const hasContentManagerProfileRole = (role) =>
  getAllowedContentManagerRoles().has(normalizeRole(role));

export const hasContentManagerClaim = (user) => {
  const allowedRoles = getAllowedContentManagerRoles();
  return extractAuthRoles(user).some((role) => allowedRoles.has(role));
};

export const resolvePreferredContentManagerRole = (user) => {
  const roles = extractAuthRoles(user);
  if (!roles.length) return null;

  const allowedRoles = getAllowedContentManagerRoles();
  const preferredRoles = [
    normalizeRole(ADMIN_REQUIRED_ROLE),
    "admin",
    "teacher",
  ];
  for (const role of preferredRoles) {
    if (role && roles.includes(role) && allowedRoles.has(role)) return role;
  }
  return roles.find((role) => allowedRoles.has(role)) ?? null;
};

export const hasContentManagerAccess = ({
  authUser,
  userData,
  rolesClaimEnforcement = ADMIN_ROLES_CLAIM_ENFORCEMENT,
} = {}) => {
  const roles = extractAuthRoles(authUser);
  if (roles.length || hasConfiguredRolesClaim(authUser)) {
    return roles.some((role) => getAllowedContentManagerRoles().has(role));
  }
  return (
    canUseContentManagerProfileFallback(authUser, rolesClaimEnforcement) &&
    hasContentManagerProfileRole(userData?.role)
  );
};
